<template>
  <div>
    <h2>{{ $t("travelService.orderParameters") }}:</h2>
    <div style="font-size: larger">
      <v-row no-gutters>
        <template v-if="type === 'flight' || type === 'low-cost'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.startLocationText" filled :label="$t('travelService.departureAirport')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.endLocationText" filled :label="$t('travelService.arrivalAirport')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.is_baggage === 1 ? $t('global.required') : $t('global.notRequired')"
              filled
              :label="$t('travelService.registeredBaggage')"
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.start_date + ', ' + getHourText(orderItem.start_time)"
              filled
              :label="$t('travelService.startDateAndHour')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.return_date != null ? orderItem.return_date + ', ' + getHourText(orderItem.return_time) : $t('travelService.oneWayTravel')"
              filled
              :label="$t('travelService.returnDateAndHour')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="allFlightClassTypes.find(v => v.value === orderItem.seat_class).text" filled :label="$t('travelService.class')" readonly />
          </v-col>

          <v-col cols="12" v-if="orderItem.additionalSegments.length > 0">
            <fieldset class="custom-fieldset">
              <legend>{{ $t("travelService.additionalSegments") }}</legend>
              <v-row v-for="(segment, index) of orderItem.additionalSegments" :key="index">
                <v-col class="px-2" md="4" cols="12">
                  <location-ajax-select :readonly="true" :label="$t('travelService.departureAirport')" :select-model="segment.start_place_id" :box="true" />
                </v-col>
                <v-col class="px-2" md="4" cols="12">
                  <location-ajax-select :readonly="true" :label="$t('travelService.arrivalAirport')" :select-model="segment.end_place_id" :box="true" />
                </v-col>
                <v-col class="px-2" md="4" cols="12">
                  <v-text-field
                    :value="segment.start_date + ', ' + getHourText(segment.start_time)"
                    filled
                    :label="$t('travelService.startDateAndHour')"
                    readonly
                  />
                </v-col>
              </v-row>
            </fieldset>
          </v-col>

          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.is_priority_boarding === 1 ? $t('global.required') : $t('global.notRequired')"
              filled
              label="Priority boarding/Fast track"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.preferred_seat" filled :label="$t('travelService.preferredSeat')" readonly />
          </v-col>
          <v-col class="px-2" md="8" cols="12" v-if="orderItem.is_additional_services">
            <v-text-field :value="orderItem.additional_services" filled :label="$t('travelService.additionalServicesDetails')" readonly />
          </v-col>
        </template>

        <template v-if="type === 'car'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.startLocationText" filled :label="$t('travelService.pickupPlace')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.endLocationText != null ? orderItem.endLocationText : $t('travelService.theSameAsPickup')"
              filled
              :label="$t('travelService.returnCarPlace')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="allCarTypes.find(v => v.value === orderItem.car_class) != null ? allCarTypes.find(v => v.value === orderItem.car_class).text : null"
              filled
              :label="$t('travelService.carClass')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.start_date + ', ' + getHourText(orderItem.start_time)"
              filled
              :label="$t('travelService.pickupDateAndPreferredHour')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.end_date != null ? orderItem.end_date + ', ' + getHourText(orderItem.end_time) : ''"
              filled
              :label="$t('travelService.returnDateAndPreferredTime')"
              readonly
            />
          </v-col>
        </template>

        <template v-if="type === 'rail'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.startLocationText" filled :label="$t('travelService.departureRailPlace')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.endLocationText" filled :label="$t('travelService.arrivalRailPlace')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="allRailClassTypes.find(v => v.value === orderItem.seat_class).text" filled :label="$t('travelService.class')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.start_date + ', ' + getHourText(orderItem.start_time)"
              filled
              :label="$t('travelService.departureRailDateAndPreferredTime')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.return_date != null ? orderItem.return_date + ', ' + getHourText(orderItem.return_time) : 'Podróż w jedną stronę'"
              filled
              :label="$t('travelService.returnRailDateAndPreferredTime')"
              readonly
            />
          </v-col>
        </template>

        <template v-if="type === 'hotel'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.locationText" filled :label="$t('travelService.preferredHotelPlace')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="allRoomTypes.find(v => v.value === orderItem.room_type) != null ? allRoomTypes.find(v => v.value === orderItem.room_type).text : null"
              filled
              :label="$t('travelService.roomType')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.parking_required === 1 ? $t('global.required') : $t('global.notRequired')" filled label="Parking" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.start_date + ', ' + getHourText(orderItem.start_time)"
              filled
              :label="$t('travelService.accommodationDateAndPreferredTime')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.end_date" filled :label="$t('travelService.outDate')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.is_breakfast ? $t('global.required') : $t('global.notRequired')" filled :label="$t('global.breakfast')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.is_free_cancellation ? $t('global.required') : $t('global.notRequired')"
              filled
              :label="$t('travelService.freeCancel')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="orderItem.currency == null ? $t('global.standard') : orderItem.currency"
              filled
              :label="$t('travelService.preferredCurrency')"
              readonly
            />
          </v-col>
          <v-col class="px-2" md="8" cols="12" v-if="orderItem.is_additional_services">
            <v-text-field :value="orderItem.additional_services" filled :label="$t('travelService.additionalServices')" readonly />
          </v-col>
        </template>

        <template v-if="type === 'insurance'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="$t('travelService.types.insurance')" filled :label="$t('booking.other.serviceName')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.locationText" filled :label="$t('global.place')" readonly />
          </v-col>
          <v-col class="px-2" md="4" cols="12">
            <v-text-field
              :value="$t('global.fromDateShort') + ' ' + orderItem.start_date + ' ' + $t('global.toDateShort') + ' ' + orderItem.end_date"
              filled
              :label="$t('travelService.insuranceDates')"
              readonly
            />
          </v-col>
        </template>

        <template v-if="type === 'other'">
          <v-col class="px-2" md="4" cols="12">
            <v-text-field :value="orderItem.name" filled :label="$t('booking.other.serviceName')" readonly />
          </v-col>
        </template>

        <v-col class="px-2" md="4" cols="12" v-if="!!orderItem.cost_centre">
          <v-text-field :value="orderItem.cost_centre" filled :label="$t('global.costCentre')" readonly />
        </v-col>
      </v-row>

      <admin-passengers-data-preview :users="orderItem.users" />

      <admin-travel-service-additional-params-preview :additional-parameters="orderItem" />

      <v-col class="px-2" cols="12" v-if="orderItem.comment != null && orderItem.comment.length > 0">
        <v-textarea :value="orderItem.comment" filled :label="$t('global.comment')" auto-grow :rows="1" readonly outlined />
      </v-col>
    </div>
  </div>
</template>

<script>
import AdminPassengersDataPreview from "./AdminPassengersDataPreview";
import AdminTravelServiceAdditionalParamsPreview from "../AdminTravelServiceAdditionalParamsPreview";
import { allCarTypes, allFlightClassTypes, allRailClassTypes, allRoomTypes } from "../../../staticData/TravelServiceTypes";
import LocationAjaxSelect from "../../CustomInputComponents/LocationAjaxSelect";
export default {
  name: "OrderItemParameters",
  components: { LocationAjaxSelect, AdminTravelServiceAdditionalParamsPreview, AdminPassengersDataPreview },
  props: {
    orderItem: {
      required: true
    },
    type: {
      required: true
    }
  },
  computed: {
    allCarTypes,
    allRailClassTypes,
    allFlightClassTypes,
    allRoomTypes
  },
  methods: {
    getHourText(hour) {
      return hour != null && hour.length > 3 ? hour : this.$t("travelService.timeNotSet");
    }
  }
};
</script>

<style scoped></style>
